import React, { useMemo } from "react";

import cn from "classnames";

import { Box, boxForwardRef } from "../../../helpers/common/Box/Box";
import { useMouseDownAnimation } from "../../../hooks/useMouseDownAnimation";
import { getChildrenRecord } from "../../../utils/react";

import type { ChipProps } from "./types";
import type { BoxComponentWithDefault } from "../../../helpers/common/Box/Box";

import classes from "./ChipSuggest.styl";

const ANIMATION_DURATION = 300;
const CHIP_SUGGEST_DISPLAY_NAME = "ChipSuggest";

/**
 * Используется в роли подсказки, подставляют слова в input.
 *
 * В качестве `as` ожидаем, что будет передан 'button', 'a' или компонент, реализующий функцию навигации (например, `RoutingLink` на большом вебе).
 * Более подробную информацию можно найти [тут](/docs/документация-разработчикам-дс-инструкция-по-разработке-полиморфных-компонентов--docs).
 *
 * <a href="https://www.figma.com/design/LYZAXE33pUg2cSL5mmUHYq/%F0%9F%8D%91-Buttons-%26-Tabs-%26-Chips?m=auto&node-id=2-2631&t=3ryRFv5yEABAv4kK-1" target="_blank">Спека</a>
 * @constructor
 */

export const ChipSuggest = boxForwardRef<
    BoxComponentWithDefault<"button", ChipProps>
>(
    (
        {
            as = "button",
            customColorsStyles,
            view = "primary",
            "data-tsid": tsid,
            children,
            ...props
        },
        ref,
    ) => {
        const { Label } = useMemo(
            () =>
                getChildrenRecord(children, "ChipSuggest", {
                    Label: {},
                }),
            [children],
        );

        const { clicked, mousedownHandler } = useMouseDownAnimation();

        const chipSuggestClassName = cn(
            classes["chip-suggest"],
            classes[`__view-${view}`],
            {
                [classes[`__clicked`]]: clicked,
            },
            customColorsStyles,
        );

        return (
            <div
                className={classes["wrapper"]}
                data-tsid={tsid}
                data-uikit={CHIP_SUGGEST_DISPLAY_NAME}
            >
                <Box
                    as={as}
                    {...props}
                    type="button"
                    className={chipSuggestClassName}
                    onMouseDown={mousedownHandler}
                    style={{ transitionDuration: `${ANIMATION_DURATION}ms` }}
                    ref={ref}
                >
                    <span className={classes["label"]}>{Label}</span>
                </Box>
                <div className={classes["chip-suggest-outline"]} />
            </div>
        );
    },
);

ChipSuggest.displayName = CHIP_SUGGEST_DISPLAY_NAME;

export default ChipSuggest;
